import React, { useState, useEffect } from "react";
import GraphiqueBarres from "../components/GraphiqueBarres";
import GraphiqueCourbe from "../components/GraphiqueCourbe";
import { getJiraDashboardCollaborateur } from "../services/api";

// ===================================================================
// The Crowd v2 — Mes tickets
//
// Adaptation v2 de pages/DashboardJira.js : meme appel API, memes
// indicateurs et graphiques. Disposition plein ecran : indicateurs en
// tete, graphiques en grille, courbe mensuelle sur toute la largeur.
//
// Couleurs : les barres par file sont neutres (le libelle suffit a les
// distinguer) ; les priorites prennent les couleurs de feedback du
// theme, redefinies en mode clair.
// ===================================================================

const FILE_LABELS = {
  HEL: "Helpdesk",
  GDC: "Gestion des Comptes",
  DEVMGT: "DeviceMgmt",
  CHG: "Change Management",
};

// Cinq priorites Jira sur trois couleurs de feedback + neutre
const TONALITES_PRIORITE = {
  Highest: "danger",
  High: "danger",
  Medium: "warning",
  Low: "success",
  Lowest: "neutre",
};

function MesTicketsV2({ collaborateur }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!collaborateur || !collaborateur.jira_display_name) {
      setLoading(false);
      return;
    }
    getJiraDashboardCollaborateur(collaborateur.jira_display_name)
      .then(setData)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [collaborateur]);

  const entete = (
    <header className="v2-module-entete">
      <h1 className="v2-module-titre">Mes tickets</h1>
      <p className="v2-sous-titre">Vos tickets Jira : résolus, en cours et répartition.</p>
    </header>
  );

  if (!collaborateur) {
    return (
      <div className="v2-page v2-page-module">
        {entete}
        <div className="v2-msg v2-msg-erreur">Compte non configuré.</div>
      </div>
    );
  }

  if (!collaborateur.jira_display_name) {
    return (
      <div className="v2-page v2-page-module">
        {entete}
        <div className="v2-panneau">
          <p className="v2-vide">
            Votre nom Jira n'est pas encore configuré. Contactez votre manager pour qu'il renseigne le champ
            « Nom Jira » dans votre profil.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="v2-page v2-page-module">
        {entete}
        <div className="v2-chargement">Chargement des données Jira…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="v2-page v2-page-module">
        {entete}
        <div className="v2-msg v2-msg-erreur">Erreur : {error}</div>
      </div>
    );
  }

  if (!data) return null;

  const {
    totaux,
    resolus_par_file,
    en_cours_par_file,
    evolution_mensuelle,
    temps_moyen_resolution,
    repartition_type,
    repartition_priorite,
  } = data;

  return (
    <div className="v2-page v2-page-module">
      {entete}

      <div className="v2-tickets-stats">
        <div className="v2-stat">
          <span className="v2-stat-libelle">Résolus</span>
          <span className="v2-stat-valeur">{totaux.total_resolus}</span>
        </div>
        <div className="v2-stat">
          <span className="v2-stat-libelle">En cours</span>
          <span className="v2-stat-valeur">{totaux.total_en_cours}</span>
        </div>
        <div className="v2-stat">
          <span className="v2-stat-libelle">Taux de résolution</span>
          <span className="v2-stat-valeur">{(totaux.taux_resolution ?? 0).toFixed(1)} %</span>
        </div>
      </div>

      <div className="v2-tickets-grille">
        {evolution_mensuelle && evolution_mensuelle.length >= 2 && (
          <div className="v2-panneau v2-tickets-large">
            <h3 className="v2-panneau-titre">Évolution mensuelle des tickets résolus</h3>
            <GraphiqueCourbe
              data={evolution_mensuelle}
              xKey="mois"
              yKey="total_resolus"
              formatX={(m) => (m ? m.substring(5) : "")}
              unite=" tickets"
            />
          </div>
        )}

        <div className="v2-panneau">
          <h3 className="v2-panneau-titre">Résolus par file</h3>
          <GraphiqueBarres data={resolus_par_file} labelKey="file_jira" valueKey="total" libelles={FILE_LABELS} />
        </div>

        <div className="v2-panneau">
          <h3 className="v2-panneau-titre">En cours par file</h3>
          <GraphiqueBarres data={en_cours_par_file} labelKey="file_jira" valueKey="total" libelles={FILE_LABELS} />
        </div>

        {repartition_type && (
          <div className="v2-panneau">
            <h3 className="v2-panneau-titre">Par type</h3>
            <GraphiqueBarres data={repartition_type} labelKey="type_ticket" valueKey="total" />
          </div>
        )}

        {repartition_priorite && (
          <div className="v2-panneau">
            <h3 className="v2-panneau-titre">Par priorité</h3>
            <GraphiqueBarres
              data={repartition_priorite}
              labelKey="priorite"
              valueKey="total"
              tonalites={TONALITES_PRIORITE}
            />
          </div>
        )}

        {temps_moyen_resolution && temps_moyen_resolution.length > 0 && (
          <div className="v2-panneau">
            <h3 className="v2-panneau-titre">Temps moyen de résolution (heures)</h3>
            <GraphiqueBarres
              data={temps_moyen_resolution}
              labelKey="file_jira"
              valueKey="heures_moyenne"
              libelles={FILE_LABELS}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default MesTicketsV2;
